// Waveform — канвас-волна со scrubbing'ом. ОДИН на все плееры аудио.
//
// До сведения математика жила в двух копиях (`recap-player-bar.tsx` и
// `layout/audio-mini-player.tsx`), и каждая копия несла свой дефект:
//   - мини-плеер зашивал цвета столбиков строками oklch → волна не следовала
//     светлой/тёмной теме (нарушение frontend-quality §9);
//   - у бара резюме не было ResizeObserver → при смене ширины (открытие или
//     закрытие панели репетитора) растягивался старый пиксельный буфер canvas.
// Здесь оба вылечены разом: цвета — из токенов темы (`useWaveformColors`),
// ширина — через ResizeObserver.
//
// Хозяин задаёт только геометрию (высоту через className/style) и то, активна
// ли отрисовка.

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDurationHms } from "@/components/recordings/_format";
import { cn } from "@/lib/cn";
import { useWaveformColors } from "./_waveform-colors";

/**
 * Плотность отрисовки: пики высокого разрешения усредняются под фактическую
 * ширину canvas, шаг столбика фиксирован в CSS-px. Без этого узкий плеер
 * выглядит «толстыми палками», а широкий — гребёнкой.
 */
export const PEAKS_RESOLUTION = 240;
const BAR_PITCH_CSS_PX = 5;

/**
 * Амплитуда столбика, когда пиков ещё нет (декодирование не закончилось).
 * Копии расходились на глаз (0.18/0.08 против 0.2/0.1) — это был шум
 * копипасты, а не решение; берём одно значение.
 */
const IDLE_AMP = 0.18;
const MIN_AMP = 0.08;

export interface WaveformProps {
	/** Пики [0..1] длиной `PEAKS_RESOLUTION`; `null` — ещё не декодированы. */
	peaks: number[] | null;
	positionSec: number;
	durationSec: number;
	/** Клик/перетаскивание по волне — абсолютная позиция в секундах. */
	onSeek: (sec: number) => void;
	/**
	 * Перерисовывать ли волну. `false` — плеер скрыт или уезжает: canvas
	 * сохраняет последний кадр (иначе на время exit-анимации волна моргает
	 * пустотой), а ResizeObserver не считает ширину скрытого элемента.
	 */
	active?: boolean;
	/** Высота и скругления — от хозяина: бар резюме выше мини-плеера. */
	className?: string;
	style?: React.CSSProperties;
}

export function Waveform({
	peaks,
	positionSec,
	durationSec,
	onSeek,
	active = true,
	className,
	style,
}: WaveformProps): React.ReactElement {
	const { t } = useTranslation("audio");
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	// Цвета waveform читаются из CSS-токенов темы (light/dark) — не hardcode.
	const waveColors = useWaveformColors();
	// Ширина canvas в CSS-px. Меняется, когда плеер сжимается/расширяется
	// (открытие/закрытие панели репетитора двигает --side-panel-width). Входит в
	// deps отрисовки → волна перестраивается под новую ширину (одинаковая
	// толщина столбиков, меняется их количество), а не растягивает старый буфер.
	const [canvasW, setCanvasW] = useState(0);

	useEffect(() => {
		if (!active) return;
		const canvas = canvasRef.current;
		if (!canvas) return;
		const update = (): void => setCanvasW(canvas.clientWidth);
		update();
		const observer = new ResizeObserver(update);
		observer.observe(canvas);
		return () => observer.disconnect();
	}, [active]);

	const seekFromPointer = useCallback(
		(clientX: number) => {
			const canvas = canvasRef.current;
			if (!canvas || durationSec <= 0) return;
			const rect = canvas.getBoundingClientRect();
			const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
			onSeek(ratio * durationSec);
		},
		[durationSec, onSeek],
	);

	const onPointerDown = useCallback(
		(e: React.PointerEvent<HTMLCanvasElement>) => {
			if (durationSec <= 0) return;
			(e.target as Element).setPointerCapture?.(e.pointerId);
			seekFromPointer(e.clientX);
		},
		[durationSec, seekFromPointer],
	);

	const onPointerMove = useCallback(
		(e: React.PointerEvent<HTMLCanvasElement>) => {
			if (e.buttons === 0 && e.pointerType === "mouse") return;
			if (!(e.target as Element).hasPointerCapture?.(e.pointerId)) return;
			seekFromPointer(e.clientX);
		},
		[seekFromPointer],
	);

	const onPointerUp = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
		if ((e.target as Element).hasPointerCapture?.(e.pointerId)) {
			(e.target as Element).releasePointerCapture?.(e.pointerId);
		}
	}, []);

	/**
	 * Геометрия буфера: считается ТОЛЬКО при смене размера, не на каждый кадр.
	 *
	 * Раньше отрисовка начиналась с `getBoundingClientRect()` и присвоения
	 * `canvas.width/height`, а эффект зависел от `positionSec` — то есть четыре
	 * раза в секунду браузер принудительно пересчитывал layout и сбрасывал
	 * битмап. В консоли это шло сплошной лентой «Forced reflow while executing
	 * JavaScript took 30-60ms» (жалоба 2026-08-19), а главный поток вставал на
	 * треть кадра при каждом тике позиции.
	 */
	const geometryRef = useRef({ barCount: 20, gap: 1, bw: 2, dpr: 1 });
	/** Индекс последнего закрашенного столбика — тоньше него перерисовывать нечего. */
	const lastBarRef = useRef(-1);
	// biome-ignore lint/correctness/useExhaustiveDependencies: canvasW — триггер пересчёта, размеры берутся из DOM внутри эффекта.
	useEffect(() => {
		if (!active) return;
		const canvas = canvasRef.current;
		if (!canvas) return;
		const dpr = window.devicePixelRatio || 1;
		const rect = canvas.getBoundingClientRect();
		canvas.width = Math.floor(rect.width * dpr);
		canvas.height = Math.floor(rect.height * dpr);
		const barCount = Math.max(20, Math.floor(rect.width / BAR_PITCH_CSS_PX));
		const gap = 1 * dpr;
		geometryRef.current = {
			barCount,
			gap,
			bw: Math.max(1.5 * dpr, canvas.width / barCount - gap),
			dpr,
		};
		lastBarRef.current = -1;
	}, [canvasW, active]);

	// Пики приходят асинхронно (декодирование), цвета — со сменой темы. И то и
	// другое меняет КАРТИНКУ при неизменном прогрессе, поэтому снимаем защёлку
	// столбика: без этого волна осталась бы на idle-амплитуде до следующего
	// столбика прогресса.
	useEffect(() => {
		lastBarRef.current = -1;
	}, [peaks, waveColors]);

	useEffect(() => {
		if (!active) return;
		const canvas = canvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;
		// Ни одного чтения layout: `canvas.width/height` — атрибуты буфера,
		// геометрия посчитана эффектом размера выше.
		const w = canvas.width;
		const h = canvas.height;
		if (w === 0 || h === 0) return;
		const { barCount, gap, bw } = geometryRef.current;
		const progress = durationSec > 0 ? positionSec / durationSec : 0;
		const progressX = w * progress;
		// Волна дискретна по столбикам: пока прогресс не перешёл на следующий,
		// картинка не меняется — перерисовывать нечего.
		const bar = Math.floor(progress * barCount);
		if (bar === lastBarRef.current) return;
		lastBarRef.current = bar;

		ctx.clearRect(0, 0, w, h);

		const peaksPerBar = peaks ? peaks.length / barCount : 0;
		for (let i = 0; i < barCount; i++) {
			let amp = IDLE_AMP;
			if (peaks && peaksPerBar > 0) {
				const start = Math.floor(i * peaksPerBar);
				const end = Math.min(peaks.length, Math.max(start + 1, Math.floor((i + 1) * peaksPerBar)));
				let sum = 0;
				let count = 0;
				for (let j = start; j < end; j++) {
					sum += peaks[j] ?? 0;
					count += 1;
				}
				amp = count > 0 ? Math.max(MIN_AMP, sum / count) : IDLE_AMP;
			}
			const bh = Math.max(1.5 * geometryRef.current.dpr, h * amp);
			const x = i * (bw + gap);
			const y = h / 2 - bh / 2;
			ctx.fillStyle = x < progressX ? waveColors.played : waveColors.track;
			ctx.fillRect(x, y, bw, bh);
		}
		// `canvasW` в зависимостях обязателен: эффект геометрии выше сбрасывает
		// битмап (присвоение `canvas.width`), и без перерисовки волна исчезла бы
		// после изменения ширины на паузе — позиция-то не меняется.
	}, [positionSec, durationSec, peaks, waveColors, active, canvasW]);

	return (
		<canvas
			ref={canvasRef}
			style={{ width: "100%", ...style }}
			className={cn("block cursor-pointer touch-none select-none", className)}
			onPointerDown={onPointerDown}
			onPointerMove={onPointerMove}
			onPointerUp={onPointerUp}
			onPointerCancel={onPointerUp}
			role="slider"
			aria-label={t("miniPlayer.position")}
			aria-valuemin={0}
			aria-valuemax={Math.max(1, Math.round(durationSec))}
			aria-valuenow={Math.round(positionSec)}
			aria-valuetext={formatDurationHms(positionSec)}
			tabIndex={0}
		/>
	);
}
